/**
 * Field definitions + compute functions for the 10 landlord calculators.
 * Ported verbatim (same formulas) from the design handoff's Rentora Calculator.dc.html.
 */

export interface CalcField {
  key: string;
  label: string;
  prefix?: string;
  suffix?: string;
  value: number;
}

export interface CalcResult {
  resultLabel: string;
  resultValue: string;
  breakdown: [string, string][];
  note: string;
}

export interface CalcDef {
  label: string;
  fields: CalcField[];
  compute: (v: Record<string, number>) => CalcResult;
}

const gbp = (n: number): string => '£' + Math.round(n).toLocaleString();

export const CALCULATOR_DEFS: Record<string, CalcDef> = {
  'rental-yield-calculator': {
    label: 'Rental Yield',
    fields: [
      { key: 'price', label: 'Property price', prefix: '£', value: 250000 },
      { key: 'rent', label: 'Monthly rent', prefix: '£', value: 1200 },
      { key: 'costs', label: 'Annual running costs', prefix: '£', value: 2500 },
    ],
    compute: (v) => {
      const gross = ((v.rent * 12) / v.price) * 100;
      const net = ((v.rent * 12 - v.costs) / v.price) * 100;
      return {
        resultLabel: 'Net rental yield',
        resultValue: net.toFixed(1) + '%',
        breakdown: [
          ['Gross yield', gross.toFixed(1) + '%'],
          ['Annual rent', gbp(v.rent * 12)],
          ['Net income', gbp(v.rent * 12 - v.costs)],
        ],
        note: 'Gross yield is annual rent ÷ price. Net yield deducts running costs. UK BTL investors typically target 5–8% gross.',
      };
    },
  },
  'stamp-duty-calculator': {
    label: 'Stamp Duty (SDLT)',
    fields: [{ key: 'price', label: 'Property price', prefix: '£', value: 250000 }],
    compute: (v) => {
      const p = v.price;
      const bands: [number, number][] = [
        [125000, 0],
        [250000, 0.02],
        [925000, 0.05],
        [1500000, 0.1],
        [Infinity, 0.12],
      ];
      let last = 0;
      let base = 0;
      for (const [cap, rate] of bands) {
        const amt = Math.max(0, Math.min(p, cap) - last);
        base += amt * rate;
        last = cap;
        if (p <= cap) break;
      }
      const sur = p * 0.03;
      const total = base + sur;
      return {
        resultLabel: 'Total SDLT (with 3% surcharge)',
        resultValue: gbp(total),
        breakdown: [
          ['Standard SDLT', gbp(base)],
          ['3% BTL surcharge', gbp(sur)],
          ['Effective rate', ((total / p) * 100).toFixed(1) + '%'],
        ],
        note: 'Includes the 3% additional-property surcharge that applies to most buy-to-let and second-home purchases in England & NI.',
      };
    },
  },
  'roi-calculator': {
    label: 'Property ROI',
    fields: [
      { key: 'price', label: 'Property price', prefix: '£', value: 250000 },
      { key: 'deposit', label: 'Deposit', prefix: '£', value: 62500 },
      { key: 'rent', label: 'Monthly rent', prefix: '£', value: 1200 },
      { key: 'costs', label: 'Monthly costs (incl. mortgage)', prefix: '£', value: 800 },
    ],
    compute: (v) => {
      const annualProfit = (v.rent - v.costs) * 12;
      const roi = (annualProfit / v.deposit) * 100;
      return {
        resultLabel: 'Cash-on-cash ROI',
        resultValue: roi.toFixed(1) + '%',
        breakdown: [
          ['Annual profit', gbp(annualProfit)],
          ['Monthly cash flow', gbp(v.rent - v.costs)],
          ['Cash invested', gbp(v.deposit)],
        ],
        note: 'Cash-on-cash ROI = annual profit ÷ cash invested. It ignores capital growth, which for many UK landlords is the larger long-term return.',
      };
    },
  },
  'mortgage-calculator': {
    label: 'BTL Mortgage',
    fields: [
      { key: 'loan', label: 'Loan amount', prefix: '£', value: 187500 },
      { key: 'rate', label: 'Interest rate', suffix: '%', value: 5.5 },
      { key: 'term', label: 'Term', suffix: 'yrs', value: 25 },
    ],
    compute: (v) => {
      const io = (v.loan * (v.rate / 100)) / 12;
      const r = v.rate / 100 / 12;
      const n = v.term * 12;
      const rep = r > 0 ? (v.loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : v.loan / n;
      return {
        resultLabel: 'Interest-only / month',
        resultValue: gbp(io),
        breakdown: [
          ['Repayment / month', gbp(rep)],
          ['Interest-only / year', gbp(io * 12)],
          ['Total loan', gbp(v.loan)],
        ],
        note: 'Most UK BTL mortgages are interest-only. Lenders stress-test rental cover at ~145% of interest at a notional 5.5%+ rate.',
      };
    },
  },
  'rent-affordability-calculator': {
    label: 'Rent Affordability',
    fields: [
      { key: 'income', label: 'Tenant annual income', prefix: '£', value: 36000 },
      { key: 'rent', label: 'Monthly rent', prefix: '£', value: 1200 },
    ],
    compute: (v) => {
      const ratio = ((v.rent * 12) / v.income) * 100;
      const max = (v.income * 0.3) / 12;
      const pass = ratio <= 30;
      return {
        resultLabel: 'Rent-to-income ratio',
        resultValue: ratio.toFixed(0) + '%',
        breakdown: [
          ['30% affordability cap', gbp(max) + '/mo'],
          ['Status', pass ? 'Within guideline' : 'Above guideline'],
          ['Income multiple needed', (v.rent * 12 * (1 / 0.3)).toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })],
        ],
        note: 'The common UK guideline is that rent should not exceed 30% of gross income, or income should be ~30× the monthly rent.',
      };
    },
  },
  'property-cash-flow-calculator': {
    label: 'Cash Flow',
    fields: [
      { key: 'rent', label: 'Monthly rent', prefix: '£', value: 1200 },
      { key: 'mortgage', label: 'Monthly mortgage', prefix: '£', value: 600 },
      { key: 'costs', label: 'Other monthly costs', prefix: '£', value: 200 },
    ],
    compute: (v) => {
      const m = v.rent - v.mortgage - v.costs;
      return {
        resultLabel: 'Monthly cash flow',
        resultValue: gbp(m),
        breakdown: [
          ['Annual cash flow', gbp(m * 12)],
          ['Gross income', gbp(v.rent)],
          ['Total outgoings', gbp(v.mortgage + v.costs)],
        ],
        note: 'Positive monthly cash flow means the property pays for itself. Always model a void allowance of 1–2 months per year.',
      };
    },
  },
  'landlord-tax-estimator': {
    label: 'Landlord Tax',
    fields: [
      { key: 'rent', label: 'Annual rental income', prefix: '£', value: 14400 },
      { key: 'expenses', label: 'Allowable expenses', prefix: '£', value: 3000 },
      { key: 'mortgage', label: 'Annual mortgage interest', prefix: '£', value: 7200 },
      { key: 'rate', label: 'Your tax rate', suffix: '%', value: 40 },
    ],
    compute: (v) => {
      const profit = v.rent - v.expenses;
      const tax = profit * (v.rate / 100);
      const credit = v.mortgage * 0.2;
      const net = tax - credit;
      return {
        resultLabel: 'Estimated tax due',
        resultValue: gbp(Math.max(0, net)),
        breakdown: [
          ['Taxable profit', gbp(profit)],
          ['Tax before relief', gbp(tax)],
          ['S24 20% interest credit', gbp(credit)],
        ],
        note: "Under Section 24, mortgage interest is no longer a deductible expense — it's a 20% tax credit. This hits higher-rate landlords hardest.",
      };
    },
  },
  'portfolio-health-score': {
    label: 'Portfolio Health',
    fields: [
      { key: 'props', label: 'Number of properties', value: 8 },
      { key: 'compliant', label: 'Fully compliant properties', value: 6 },
      { key: 'late', label: 'Properties with late rent', value: 1 },
    ],
    compute: (v) => {
      const comp = v.props ? v.compliant / v.props : 0;
      const rent = v.props ? 1 - v.late / v.props : 1;
      const score = Math.round((comp * 0.7 + rent * 0.3) * 100);
      return {
        resultLabel: 'Portfolio health score',
        resultValue: score + '/100',
        breakdown: [
          ['Compliance', Math.round(comp * 100) + '%'],
          ['Rent on time', Math.round(rent * 100) + '%'],
          ['At-risk properties', String(v.props - v.compliant)],
        ],
        note: 'MillOS computes this live across your whole portfolio — weighting certificate compliance at 70% and rent performance at 30%.',
      };
    },
  },
  'epc-expiry-checker': {
    label: 'EPC Expiry',
    fields: [
      { key: 'years', label: 'Years since EPC issued', suffix: 'yrs', value: 7 },
      { key: 'band', label: 'Current EPC band (1=A … 7=G)', value: 4 },
    ],
    compute: (v) => {
      const left = Math.max(0, 10 - v.years);
      const meets = v.band <= 5;
      const c = v.band <= 3;
      return {
        resultLabel: 'Years until EPC expires',
        resultValue: left + ' yrs',
        breakdown: [
          ['Meets MEES (band E)', meets ? 'Yes' : 'No — cannot let'],
          ['Meets 2028 band C', c ? 'Yes' : 'Not yet'],
          ['Renewal window', left <= 1 ? 'Renew now' : 'On track'],
        ],
        note: 'EPCs last 10 years. Minimum band is E today; band C is proposed for new tenancies from 2028. Below E, you cannot legally let.',
      };
    },
  },
  'certificate-expiry-calculator': {
    label: 'Certificate Expiry',
    fields: [
      { key: 'gas', label: 'Months since Gas Safety', suffix: 'mo', value: 8 },
      { key: 'eicr', label: 'Years since EICR', suffix: 'yrs', value: 3 },
      { key: 'epc', label: 'Years since EPC', suffix: 'yrs', value: 6 },
    ],
    compute: (v) => {
      const gas = Math.max(0, 12 - v.gas);
      const eicr = Math.max(0, 5 - v.eicr);
      const epc = Math.max(0, 10 - v.epc);
      const next = Math.min(gas, eicr * 12, epc * 12);
      return {
        resultLabel: 'Next renewal due in',
        resultValue: next + ' months',
        breakdown: [
          ['Gas Safety', gas + ' mo left'],
          ['EICR', eicr + ' yrs left'],
          ['EPC', epc + ' yrs left'],
        ],
        note: 'Book each renewal 60–90 days before expiry. MillOS does this automatically the moment a certificate is uploaded.',
      };
    },
  },
};
